import {useVNextAudit, AuditMonth, AuditCheck} from "@hooks/use-vnext";

interface AuditTabProps {
    workspaceId: string;
    scenarioId: string;
}

function fmt(v: number | null): string {
    if (v === null || v === undefined) return "—";
    return v.toLocaleString("en-AU", {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

function CheckBadge({pass}: {pass: boolean | null}) {
    if (pass === null) return <span className="text-xs text-gray-400">N/A</span>;
    return pass ? (
        <span className="inline-block px-1.5 py-0.5 text-xs font-medium rounded bg-green-100 text-green-800">PASS</span>
    ) : (
        <span className="inline-block px-1.5 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800">FAIL</span>
    );
}

function MonthCard({month}: {month: AuditMonth}) {
    return (
        <div className={`border rounded-lg p-4 ${month.pass ? "border-green-200 bg-green-50/30" : "border-red-200 bg-red-50/30"}`}>
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-sm">{month.label}</h3>
                <CheckBadge pass={month.pass} />
            </div>
            <div className="flex gap-2 mb-3 text-[10px]">
                <span className={month.has_pnl_snapshot ? "text-green-600" : "text-gray-400"}>
                    P&L {month.has_pnl_snapshot ? "OK" : "Missing"}
                </span>
                <span className={month.has_cash_snapshot ? "text-green-600" : "text-gray-400"}>
                    Cash {month.has_cash_snapshot ? "OK" : "Missing"}
                </span>
                <span className={month.has_bs_snapshot ? "text-green-600" : "text-gray-400"}>
                    BS {month.has_bs_snapshot ? "OK" : "Missing"}
                </span>
            </div>
            <table className="w-full text-xs">
                <thead>
                    <tr className="text-left text-gray-500 border-b">
                        <th className="py-1 font-medium">Metric</th>
                        <th className="py-1 font-medium text-right">Xero</th>
                        <th className="py-1 font-medium text-right">Engine</th>
                        <th className="py-1 font-medium text-right">Delta</th>
                        <th className="py-1 font-medium text-center">Status</th>
                    </tr>
                </thead>
                <tbody>
                    {month.checks.map((c: AuditCheck, i: number) => (
                        <tr key={i} className="border-b border-gray-100">
                            <td className="py-1">{c.label}</td>
                            <td className="py-1 text-right font-mono">{fmt(c.xero)}</td>
                            <td className="py-1 text-right font-mono">{fmt(c.engine)}</td>
                            <td className={`py-1 text-right font-mono ${c.delta && Math.abs(c.delta) > 1 ? "text-red-600 font-semibold" : ""}`}>
                                {fmt(c.delta)}
                            </td>
                            <td className="py-1 text-center">
                                <CheckBadge pass={c.pass} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {month.checks.some((c) => c.note && c.pass === false) && (
                <div className="mt-2 text-xs text-red-600">
                    {month.checks.filter((c) => c.pass === false).map((c, i) => (
                        <div key={i}>{c.label}: {c.note}</div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function AuditTab({workspaceId}: AuditTabProps) {
    const {audit, isLoading, error, refresh} = useVNextAudit(workspaceId);

    if (isLoading) {
        return (
            <div className="p-6 text-center text-gray-400 text-sm">Running accuracy audit...</div>
        );
    }

    if (error) {
        return (
            <div className="p-6 text-center text-red-500 text-sm">
                Error: {error}
                <button onClick={() => refresh()} className="ml-2 underline">Retry</button>
            </div>
        );
    }

    if (!audit) {
        return <div className="p-6 text-center text-gray-400 text-sm">No audit data available.</div>;
    }

    const passCount = audit.months.filter((m) => m.pass).length;
    const failCount = audit.months.filter((m) => !m.pass).length;

    return (
        <div className="space-y-4 p-2">
            {/* Summary header */}
            <div className="flex items-center gap-4 p-4 rounded-lg border bg-white">
                <div className={`text-2xl font-bold ${audit.overall_pass ? "text-green-600" : "text-red-600"}`}>
                    {audit.overall_pass ? "ALL PASS" : "ISSUES FOUND"}
                </div>
                <div className="text-sm text-gray-600">
                    <span className="font-medium">{audit.months_audited}</span> months audited
                    {" | "}
                    <span className="text-green-600 font-medium">{passCount} passed</span>
                    {failCount > 0 && (
                        <>
                            {" | "}
                            <span className="text-red-600 font-medium">{failCount} failed</span>
                        </>
                    )}
                </div>
                <div className="text-xs text-gray-400 ml-auto">
                    Tolerance: ${audit.tolerance} | {audit.months_with_pnl_snapshot} P&L snapshots | {audit.months_with_cash_snapshot} Cash snapshots
                </div>
                <button
                    onClick={() => refresh()}
                    className="text-xs px-3 py-1 rounded border hover:bg-gray-50"
                >
                    Re-run
                </button>
            </div>

            {/* Month cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {audit.months.map((month) => (
                    <MonthCard key={month.month_key} month={month} />
                ))}
            </div>

            {audit.months.length === 0 && (
                <div className="text-center text-gray-400 text-sm py-8">
                    No actual months found. Run a Xero sync first.
                </div>
            )}
        </div>
    );
}
